import { Component, EventEmitter, Input, OnInit, ViewChildren, ElementRef, QueryList, ChangeDetectionStrategy } from "@angular/core";
import { UploadWareBaseComponent } from "@mypxplat/xplat/features";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FileUploader, FileItem } from "ng2-file-upload";
import { StorageService, StorageKeys, UserService, environment, ExchangeService, WindowService, HelperService } from "@mypxplat/xplat/core";
import { Router } from "@angular/router";
import { AppService } from "@mypxplat/xplat/web/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-upload-ware",
  templateUrl: "upload-ware.component.html",
  standalone: false,
})
export class UploadWareComponent extends UploadWareBaseComponent implements OnInit {
  @Input() editing: any;
  @Input() creator_id: any;
  public original: any;
  public hasBaseDropZoneOver = false;
  public hasBaseDropZoneOverPreview = false;
  // Retained for compatibility with the existing drag-and-drop template markup, which uses
  // `<input ng2FileSelect>`. The actual upload runs through `performUpload()` via a
  // presigned S3 PUT — this FileUploader instance is only used as a form-input adapter and
  // never actually posts anything itself (uploadAll is never called).
  public uploader: FileUploader = new FileUploader({
    isHTML5: true,
    url: "about:blank",
    method: "POST",
    queueLimit: 1,
  });

  public previewUploader: FileUploader;
  public uploadingComplete: boolean = false;
  public uploadProgress: number = 0;
  public previewUploadingComplete: boolean = false;
  public uploadingPreviewFile: any;
  public previewFileResponse: any;
  public uploadingFile: any;
  public fileResponse: any;
  public newAsset: any;
  public saving: boolean = false;
  public saved: boolean = false;
  public audioPreviews: Array<any> = [];
  public newAssetIDs: any = {};
  public extraData: any = {
    pitchlist: "Device",
    ioconfig: "Device",
    macropage: "CategoryAttribute",
    pattern: "CategoryAttribute",
    multisample: "CategoryAttribute",
    keyswitch: "Device",
  };
  public allowedExtensions: Array<string>;
  public fileName: string = "No file chosen";

  constructor(
    public storageService: StorageService,
    public userService: UserService,
    public activeModal: NgbActiveModal,
    public exchangeService: ExchangeService,
    public router: Router,
    private _win: WindowService,
    public helperService: HelperService,
    public appService: AppService
  ) {
    super(userService);
    this.allowedExtensions = [];
    for (let i in this.exchangeService.allowedExtensions) this.allowedExtensions.push(i);
    this.uploader.onAfterAddingFile = (file) => {
      let size = file.file.size;
      let ext = file.file.name.split(".").pop();
      this.fileName = file.file.name;
      if (ext == "song" && this.user.is_sphere_admin) {
        // continue.
        this.uploadingFile = file;
        file.withCredentials = false;
      } else if (!this.exchangeService.allowedExtensions[ext]) {
        alert("The file type you are trying to upload is not supported.");
        this.uploader.removeFromQueue(file);
      } else if (size > this.exchangeService.fileSizeLimits[ext] * 1024 * 1024) {
        this.uploader.removeFromQueue(file);
        alert("The file you selected exceeds the maximum file size for this type. The largest file you can upload is " + this.exchangeService.fileSizeLimits[ext] + " MB");
      } else {
        this.uploadingFile = file;
        file.withCredentials = false;
      }
    };
    // Only used by the cloud-api fallback path (single-POST multipart). No-op under the
    // default paeapi presigned flow because that path never calls `uploader.uploadAll()`.
    this.uploader.onProgressAll = (progress: any) => {
      if (progress == 100) this.uploadingComplete = true;
    };
    this.uploader.response.subscribe((response) => {
      if (!response) return;
      try {
        this.fileResponse = JSON.parse(response);
        this.newAsset = this.fileResponse.ware;
        this.fileResponse.ware.newlyUploaded = true;
        this.exchangeService.wareDetail$.next(this.fileResponse.ware);
      } catch {
        // The new paeapi presigned flow never publishes here; only legacy cloud-api does.
      }
    });
  }

  ngOnInit() {}

  getMimeType(ext) {
    const mimetypes: any = {
      soundset: "application/x-presonus-soundset",
      pitchlist: "application/octet-stream",
      multipreset: "application/octet-stream",
      preset: "application/octet-stream",
      install: "application/x-presonus-extension",
      quantize: "application/octet-stream",
      studioonemacro: "application/octet-stream",
      colorscheme: "application/x-presonus-colorscheme+xml",
      trackpreset: "application/x-presonus-trackpreset",
      colorpalette: "application/x.ccl-colorpalette+json",
    };
    return mimetypes[ext];
  }

  getCategory(ext) {
    const categories: any = {
      soundset: "soundset",
      pitchlist: "pitchlist",
      multipreset: "fxchain",
      preset: "preset",
      install: "install",
      quantize: "quantize",
      studioonemacro: "studioonemacro",
      colorscheme: "colorscheme",
      trackpreset: "trackpreset",
      colorpalette: "colorpalette",
    };
    return categories[ext];
  }

  fileOverBase(e, type?) {
    if (type == "preview") {
      this.hasBaseDropZoneOverPreview = e;
    } else {
      this.hasBaseDropZoneOver = e;
    }
  }

  performUpload() {
    // Feature-flagged during the PRS-1991 cloud-api → paeapi migration. Flip
    // `usePaeapiForWareOperations` to false to fall back to the single-POST cloud-api
    // uploadFile endpoint if the new presigned-URL flow breaks in prod.
    if (environment.features.usePaeapiForWareOperations) {
      this.performPresignedUpload();
    } else {
      this.performLegacyCloudApiUpload();
    }
  }

  private performPresignedUpload() {
    if (!this.uploadingFile) {
      alert("Please choose a file first.");
      return;
    }
    const file: File = this.uploadingFile._file ?? this.uploadingFile.file?.rawFile ?? this.uploadingFile.file;
    if (!file) {
      alert("Could not read the selected file.");
      return;
    }
    const filename: string = file.name;
    const ext = filename.split(".").pop()!.toLowerCase();
    const mimetype = this.getMimeType(ext) || "application/octet-stream";
    const category = this.getCategory(ext) || ext;

    const stubArgs: any = {
      filename,
      filesize: file.size,
      mimetype,
    };
    if (this.editing?.id) {
      stubArgs.editing_ware_id = this.editing.id;
    } else {
      stubArgs.title = filename.replace(/\.[^.]+$/, "");
      stubArgs.category = category;
    }
    if (this.creator_id) stubArgs.creator_id = this.creator_id;

    this.uploadProgress = 0;
    // Mark the ng2-file-upload queue as uploading so the template's existing
    // `uploader.isUploading` and `item.progress` bindings keep working.
    (this.uploader as any).isUploading = true;
    if (this.uploader.queue?.[0]) (this.uploader.queue[0] as any).progress = 0;
    this.exchangeService.createWareStub(stubArgs).subscribe({
      next: (stub: any) => this.putToS3(file, filename, ext, stub),
      error: (err) => {
        (this.uploader as any).isUploading = false;
        this.appService.alertError(err);
      },
    });
  }

  private performLegacyCloudApiUpload() {
    // Fallback path used when `features.usePaeapiForWareOperations` is false — original
    // pre-PRS-1991 behavior. Single multipart POST to cloud-api/uploadFile via the
    // ng2-file-upload FileUploader. Response arrives via the `uploader.response` subscription
    // set up in the constructor.
    const headers = [
      { name: "X-API-KEY", value: environment.presonusApiKey },
      { name: "X-USER-TOKEN", value: this.helperService.token },
      { name: "X-USER-ENCRYPTED-ID", value: this.userService.user.encrypted_id },
    ];
    const options: any = {
      url: environment.cloudApiUrl + "uploadFile",
      method: "POST",
      headers,
      additionalParameter: {} as Record<string, string>,
    };
    if (this.editing) options.additionalParameter.editingWare = JSON.stringify(this.editing);
    if (this.creator_id) options.additionalParameter.creator_id = this.creator_id;
    this.uploader.setOptions(options);
    this.uploader.uploadAll();
  }

  private putToS3(file: File, filename: string, ext: string, stub: { ware_id: number; content_package_id: number; upload_url: string }) {
    // Raw XHR to the presigned URL — S3 wants a bare PUT with `Content-Type` matching
    // what the presigned URL was signed with, not a multipart wrapper. This bypasses
    // API Gateway's 10 MB body cap entirely.
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", stub.upload_url, true);
    xhr.setRequestHeader("Content-Type", this.getMimeType(ext) || "application/octet-stream");
    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      const pct = Math.round((evt.loaded / evt.total) * 100);
      this.uploadProgress = pct;
      if (this.uploader.queue?.[0]) (this.uploader.queue[0] as any).progress = pct;
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        this.uploadingComplete = true;
        this.uploadProgress = 100;
        if (this.uploader.queue?.[0]) (this.uploader.queue[0] as any).progress = 100;
        this.finalizeOnServer(stub, filename, ext);
      } else {
        (this.uploader as any).isUploading = false;
        this.appService.alertError(new Error("The direct S3 upload failed (HTTP " + xhr.status + ")."));
      }
    };
    xhr.onerror = () => {
      (this.uploader as any).isUploading = false;
      this.appService.alertError(new Error("The direct S3 upload failed (network error)."));
    };
    xhr.send(file);
  }

  private finalizeOnServer(stub: { ware_id: number; content_package_id: number }, filename: string, ext: string) {
    this.exchangeService
      .finalizeUploadedWare({
        ware_id: stub.ware_id,
        content_package_id: stub.content_package_id,
        filename,
        ext,
        editing: !!this.editing?.id,
      })
      .subscribe({
        next: (response: any) => {
          (this.uploader as any).isUploading = false;
          this.fileResponse = response;
          this.newAsset = response.ware;
          this.newAsset.newlyUploaded = true;
          this.exchangeService.wareDetail$.next(this.newAsset);
        },
        error: (err) => {
          (this.uploader as any).isUploading = false;
          this.appService.alertError(err);
        },
      });
  }

  saveNewAssetDetails() {
    if (this.newAsset.title && this.newAsset.description) {
      this.saving = true;
      let ware: any = {
        id: this.newAsset.id,
        title: this.newAsset.title,
        description: this.newAsset.description,
        device: this.newAsset.device,
        approved: this.newAsset.approved,
      };
      if (this.newAsset.categoryattribute) ware.categoryattribute = this.newAsset.categoryattribute;

      this.exchangeService.updateWare(ware).subscribe((result) => {
        this.saving = false;
        this.router.navigate(["../exchange/detail/", this.newAsset.id]);
        this.activeModal.close();
      });
    } else {
      alert("Please enter a title and a description.");
    }
  }

  saveAssetDetails() {
    this.saving = true;
    let ware: any = {
      id: this.editing.id,
      title: this.editing.title,
      description: this.editing.description,
      device: this.editing.device,
    };
    if (this.extraData[this.editing.extension] == "CategoryAttribute") {
      ware.categoryattribute = this.editing.category;
    }
    this.exchangeService.updateWare(ware).subscribe(
      (result) => {
        this.saving = false;
        this.activeModal.close();
      },
      (error) => {
        this.saving = false;
        this.editing.title = this.original.title;
        this.editing.description = this.original.description;
        this.appService.alertError(error);
      }
    );
  }

  viewNewAsset() {
    this.router.navigate(["../exchange/detail/", this.newAsset.id]);
    this.activeModal.close();
  }
}
